
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useUtmTracking } from "./useUtmTracking";

export const useAuthActions = (
  setIsLoading: (isLoading: boolean) => void,
  setError: (error: string | null) => void
) => {
  const { getUTMParameters } = useUtmTracking();

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const trimmedEmail = email.trim().toLowerCase();
      
      console.log("Attempting to login with email:", trimmedEmail);
      
      // Password login
      const { data, error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });
      
      if (error) {
        // Map Supabase errors to user-friendly messages
        if (error.message.includes("Invalid login credentials")) {
          throw new Error("The email or password you entered doesn't match our records. Please double-check and try again.");
        } else if (error.message.includes("Email not confirmed")) {
          throw new Error("Please verify your email address before logging in. Check your inbox for a confirmation email.");
        } else if (error.message.includes("rate limit")) {
          throw new Error("Too many login attempts. Please wait a few minutes before trying again.");
        } else {
          throw error;
        }
      }
      
      if (!data.user) {
        throw new Error("No user returned from login");
      }
      
      console.log("User logged in:", data.user.id);
      return data;
      
    } catch (error: any) {
      console.error("Login error:", error);
      setError(error.message || "Failed to login. Please check your credentials and try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, name: string, password?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const trimmedEmail = email.trim().toLowerCase();
      
      console.log("Attempting to register with email:", trimmedEmail);
      
      // Capture UTM parameters from URL if they exist
      const utmParams = getUTMParameters();
      
      // Also check for referral ID from localStorage
      const referralId = localStorage.getItem('referral_id');
      
      // Build metadata with name, UTM params, and referral_id
      const metadata = {
        name,
        ...utmParams,
        ...(referralId && { referral_id: referralId })
      };
      
      console.log("Registration with metadata:", metadata);
      
      // Use provided password or generate one if none is provided
      const finalPassword = password || generateSecurePassword();
      
      // Create new user with email and password
      const { data, error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password: finalPassword,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      });
      
      if (error) {
        // Map Supabase errors to user-friendly messages
        if (error.message.includes("User already registered")) {
          throw new Error("An account with this email already exists. Please sign in instead.");
        } else if (error.message.includes("rate limit")) {
          throw new Error("Too many registration attempts. Please wait a few minutes before trying again.");
        } else {
          throw error;
        }
      }
      
      console.log("User registered successfully:", data);
      
      // Return the user data even if email confirmation is required
      return data;
      
    } catch (error: any) {
      console.error("Registration error:", error);
      setError(error.message || "Failed to register. Please try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const generateSecurePassword = (): string => {
    const length = 16;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const specialChars = '!@#$%^&*()_+-=[]{};\'"\\:|,./<>?`~';
    
    // Ensure at least one of each character type
    let password = 
      lowercase.charAt(Math.floor(Math.random() * lowercase.length)) +
      uppercase.charAt(Math.floor(Math.random() * uppercase.length)) +
      numbers.charAt(Math.floor(Math.random() * numbers.length)) +
      specialChars.charAt(Math.floor(Math.random() * specialChars.length));
    
    // Fill the rest randomly
    const allChars = lowercase + uppercase + numbers + specialChars;
    for (let i = 4; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Shuffle the password
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      
      console.log("Attempting to sign out user");
      
      // Clear any auth data in storage before signing out
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('impersonation');
      
      // Force global scope to ensure complete logout across all tabs/windows
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error) {
        console.error("Logout error:", error);
        throw error;
      }
      
      // Clear session from local state
      console.log("User logged out successfully");
      toast.success("Successfully signed out");
      
      // Redirect to login page after successful logout
      window.location.href = '/login';
      
      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to sign out");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    login,
    register,
    logout
  };
};
